// Hierarchical section -> measure -> criteria breakdown rendered
// inside the ScoredCallsBrowser detail panel. Mirrors the layout of
// the legacy /qa-copilot/view_scores page so users moving between
// the two see the same tree.

import React, { useState } from 'react'
import classNames from 'classnames'
import { isEmpty, isNil } from 'lodash'
import {
  IconChevronDown,
  IconChevronRight,
  IconCircleCheckFilled,
  IconCircleXFilled,
  IconCircleMinus,
} from '@tabler/icons-react'

import { pctFrom, ScorePill, StatePill } from '../helpers/format'

// Measure-level condition_type (AND/OR) decides whether a missed
// criterion is a red × (loss) or grey ⊘ (informational).
const CriterionRow = ({ criterion, measureConditionType }) => {
  const isAnd = measureConditionType === 'AND'
  const skipped = !!criterion?.skipped
  const score = criterion?.score
  const scoreNum = Number(score) || 0
  const hit = !skipped && scoreNum > 0
  const isNa = !skipped && isNil(score)
  let Icon
  let cssTone
  if (skipped || isNa) {
    Icon = IconCircleMinus
    cssTone = 'informative'
  } else if (isAnd) {
    Icon = hit ? IconCircleCheckFilled : IconCircleXFilled
    cssTone = hit ? 'hit' : 'missed'
  } else {
    Icon = hit ? IconCircleCheckFilled : IconCircleMinus
    cssTone = hit ? 'hit' : 'informative'
  }
  return (
    <li className={classNames('breakdown-criterion', `is-${cssTone}`)}>
      <Icon size={14} className="breakdown-criterion-icon" />
      <span className="breakdown-criterion-name">
        {criterion?.name || criterion?.description || 'Criterion'}
      </span>
      {skipped && <StatePill kind="skipped" />}
      {isNa && <StatePill kind="n-a" />}
    </li>
  )
}

const MeasureRow = ({ measure }) => {
  const [expanded, setExpanded] = useState(false)
  const criteria = Array.isArray(measure?.criteria_scores) ? measure.criteria_scores : []
  const hasCriteria = !isEmpty(criteria)
  const pct = pctFrom(measure?.aggregate_score, measure?.possible_score)
  const measureName = measure?.name || 'Measure'
  const autoFailed = !!measure?.auto_failed
  const autoZeroed = !!measure?.auto_zeroed
  return (
    <li
      className={classNames('breakdown-measure', {
        'is-auto-fail': autoFailed,
        'is-auto-zero': autoZeroed,
      })}
    >
      <button
        type="button"
        className="breakdown-measure-row"
        onClick={() => hasCriteria && setExpanded((prev) => !prev)}
        disabled={!hasCriteria}
      >
        <span className="breakdown-row-toggle">
          {hasCriteria &&
            (expanded ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />)}
        </span>
        <span className="breakdown-measure-name-cell">
          <span className="breakdown-measure-name">{measureName}</span>
          {autoFailed && <StatePill kind="auto-fail" />}
          {!autoFailed && autoZeroed && <StatePill kind="auto-zero" />}
        </span>
        <ScorePill pct={pct} />
      </button>
      {expanded && hasCriteria && (
        <ul className="breakdown-criteria-list">
          {criteria.map((criterion, idx) => (
            <CriterionRow
              key={criterion?.id || criterion?.uuid || `${measureName}-${idx}`}
              criterion={criterion}
              measureConditionType={measure?.condition_type}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

const SectionBlock = ({ section, defaultExpanded }) => {
  const [expanded, setExpanded] = useState(!!defaultExpanded)
  const pct = pctFrom(section?.aggregate_score, section?.possible_score)
  const measures = Array.isArray(section?.measure_scores) ? section.measure_scores : []
  const sectionName = section?.name || 'Section'
  return (
    <div className="breakdown-section">
      <button
        type="button"
        className="breakdown-section-row"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <span className="breakdown-row-toggle">
          {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
        </span>
        <span className="breakdown-section-name">{sectionName}</span>
        <ScorePill pct={pct} />
      </button>
      {expanded &&
        (isEmpty(measures) ? (
          <div className="breakdown-empty">No measures scored.</div>
        ) : (
          <ul className="breakdown-measures-list">
            {measures.map((measure, idx) => (
              <MeasureRow
                key={measure?.id || measure?.uuid || `${sectionName}-${idx}`}
                measure={measure}
              />
            ))}
          </ul>
        ))}
    </div>
  )
}

export const CallScoreBreakdown = ({ scoreData }) => {
  const sections = Array.isArray(scoreData?.section_scores) ? scoreData.section_scores : []
  if (isEmpty(sections)) {
    return <div className="breakdown-empty">No section breakdown available.</div>
  }
  return (
    <div className="call-score-breakdown">
      {sections.map((section, idx) => (
        <SectionBlock
          key={section?.id || section?.uuid || `${section?.name}-${idx}`}
          section={section}
          defaultExpanded={idx === 0}
        />
      ))}
    </div>
  )
}
